import { useState } from "react";
import { Outlet, NavLink, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import {
  ChartLine, 
  Users, 
  Calendar, 
  GearSix, 
  List,
  X,
  SignIn,
  SignOut,
  User,
  Lightning,
  House,
  Trophy,
  Question,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import HelpManual from "@/components/HelpManual";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";

// Phase 1+2 refactor (2026-04-29): canonical 6-item top nav.
// "Help" is reachable from the footer; /admin is rendered as a separate
// admin button next to the nav (existing behaviour).
const getNavItems = () => [
  { path: "/", label: "Home", icon: House, end: true },
  { path: "/matches/live", label: "Matches", icon: Lightning },
  { path: "/tournaments", label: "Tournaments", icon: Calendar },
  { path: "/rankings", label: "Rankings", icon: Trophy },
  { path: "/players", label: "Players", icon: Users },
  { path: "/help", label: "Help", icon: Question },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, loading, login, logout } = useAuth();
  const isAdmin = user?.is_admin;
  const navItems = getNavItems();

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase() || '??';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img src="/logo.png" alt="Annadil Burhani DSM" className="w-10 h-10 rounded-lg object-contain" />
              <div className="hidden sm:block">
                <h1 className="font-heading text-lg font-bold text-foreground tracking-tight">
                  Annadil Burhani DSM
                </h1>
                <p className="text-xs text-muted-foreground -mt-0.5">Snooker Section</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <item.icon className="w-4 h-4" weight="bold" />
                  {item.label}
                </NavLink>
              ))}
              
              {/* Admin link - only show for admins */}
              {user?.is_admin && (
                <NavLink
                  to="/admin"
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                    }`
                  }
                  data-testid="nav-admin"
                >
                  <GearSix className="w-4 h-4" weight="bold" />
                  Admin
                </NavLink>
              )}
            </nav>

            {/* Auth Section */}
            <div className="flex items-center gap-2">
              {user && <HelpManual />}
              {user && <NotificationBell />}
              {loading ? (
                <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
              ) : user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-10 w-10 rounded-full" data-testid="user-menu-btn">
                      <Avatar className="h-10 w-10 border border-border">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-primary text-primary-foreground">
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end">
                    <div className="flex items-center gap-2 p-2">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={user.picture} />
                        <AvatarFallback>{getInitials(user.name)}</AvatarFallback>
                      </Avatar>
                      <div className="flex flex-col">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </div>
                    <DropdownMenuSeparator />
                    {user.claimed_player_id && (
                      <DropdownMenuItem asChild>
                        <Link to={`/players/${user.claimed_player_id}`} className="cursor-pointer">
                          <User className="w-4 h-4 mr-2" />
                          My Profile
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {user.is_admin && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer">
                          <GearSix className="w-4 h-4 mr-2" />
                          Admin Panel
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} className="cursor-pointer text-destructive">
                      <SignOut className="w-4 h-4 mr-2" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button onClick={login} variant="outline" className="border-primary/50 text-primary hover:bg-primary/10" data-testid="sign-in-btn">
                  <SignIn className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? (
                  <X className="w-6 h-6" weight="bold" />
                ) : (
                  <List className="w-6 h-6" weight="bold" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden border-t border-white/10 overflow-hidden"
            >
              <div className="px-4 py-2 space-y-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    end={item.path === "/"}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`
                    }
                  >
                    <item.icon className="w-5 h-5" weight="bold" />
                    {item.label}
                  </NavLink>
                ))}
                {user?.is_admin && (
                  <NavLink
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      }`
                    }
                  >
                    <GearSix className="w-5 h-5" weight="bold" />
                    Admin
                  </NavLink>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              Annadil Burhani DSM Snooker Section - Merit-Based Ranking System
            </p>
            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <Link to="/help" className="hover:text-foreground transition-colors" data-testid="footer-help-link">
                Documentation & Help
              </Link>
              <span>Developed by Blue Digital</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
